import { createActionGroup, props } from '@ngrx/store';
import {
    IAccountingFilePreview,
    IAccountingFileRefusal,
    IClosingSetup,
    IStartClosingRequest,
} from '../models/closing-setup.model';

/**
 * The Setup-Wizard's slice (REV-21354).
 *
 * Every action carries its `projectId`. A late answer is dropped against the one in state, so a
 * trustee who switches closing mid-poll never sees the previous closing's launch on the new one.
 *
 * There is no `Cancel Launch`: the pipeline runs server-side and has no cancel route. An action that
 * only stopped the polling would suggest the work stopped too.
 */
export const closingSetupActions = createActionGroup({
    source: 'ABCP Closing Setup',
    events: {
        /** The navigation gate's read on entry: until it answers, nobody knows whether the closing started. */
        'Load Setup': props<{ projectId: string }>(),
        /** `setup` is `null` when the wizard has never run for this closing. */
        'Load Setup Success': props<{ projectId: string; setup: IClosingSetup | null }>(),
        'Load Setup Failure': props<{ projectId: string; error: string }>(),

        /**
         * The poll's read (REV-22527): fresher steps for a closing whose gate is already answered.
         * Its own action because a tick must not make the closing "unknown" again: `Load Setup`
         * resets the gate to pending, and the navigation then dropped the closing's steps every 2s.
         */
        'Refresh Setup': props<{ projectId: string }>(),
        'Refresh Setup Success': props<{ projectId: string; setup: IClosingSetup | null }>(),
        'Refresh Setup Failure': props<{ projectId: string; error: string }>(),

        'Start Closing': props<{ projectId: string; request: IStartClosingRequest }>(),
        'Start Closing Success': props<{ projectId: string; setup: IClosingSetup }>(),
        'Start Closing Failure': props<{ projectId: string; error: string }>(),

        /**
         * Stops the poll the launch screen runs.
         *
         * Dispatched when the pipeline reaches a terminal state and when the screen is left. The
         * effect's `takeUntil` hangs off this, so a poll cannot outlive the component that started it.
         */
        'Stop Polling': props<{ projectId: string }>(),

        /**
         * Follows a launch that is already running — the overview after a reload, or after the
         * wizard handed over. The poll `Start Closing Success` begins, without a start.
         */
        'Watch Launch': props<{ projectId: string }>(),

        /**
         * REV-22007 — the trial balance, uploaded BEFORE the start (ADR-0020).
         *
         * Its own request rather than part of `Start Closing`: the server parses the file on upload
         * and refuses a bad one with its rows while the trustee is still on the accounting step, where
         * they can fix it. The start then only NAMES the stored file (`accountingFileId`).
         *
         * Shared with the workspace's "ERP" button (the re-import dialog), so a file closing has ONE
         * upload path, not one per screen.
         */
        'Upload Accounting File': props<{ projectId: string; file: File }>(),
        'Upload Accounting File Success': props<{ projectId: string; preview: IAccountingFilePreview }>(),
        'Upload Accounting File Failure': props<{ projectId: string; refusal: IAccountingFileRefusal }>(),
        /** Forget the uploaded file — the trustee switched route, or a dialog closed. */
        'Reset Accounting File': props<{ projectId: string }>(),
    },
});
